import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import './ERByThreeVsPy.css';
import { BIGQUERY_CONFIG } from '../config';

import { API_BASE_URL } from '../config';

// Use config values
const PROJECT_ID = BIGQUERY_CONFIG.PROJECT;
const DATASET_NAME = BIGQUERY_CONFIG.DATASET;

function ERByThreeVsPy() {
  const { token, getAuthHeader } = useAuth();
  const [summaryTable, setSummaryTable] = useState({
    ESRD: {},
    DIS: {},
    AD: {},
    ND: {},
    TOTAL: {}
  });
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Toggle states for charts
  const [cohortToggle, setCohortToggle] = useState('TOTAL'); // 'ESRD', 'DIS', 'AD', 'ND', 'TOTAL'
  const [metricToggle, setMetricToggle] = useState('Total'); // 'Total' or 'Avoidable'

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching ER PY General data from:', `${API_BASE_URL}/api/er/by3-vs-py?project=${PROJECT_ID}&dataset=${DATASET_NAME}`);
      
      const response = await axios.get(
        `${API_BASE_URL}/api/er/by3-vs-py?project=${PROJECT_ID}&dataset=${DATASET_NAME}`,
        { 
          timeout: 180000
        }
      );

      console.log('Response received:', response.data);
      console.log('Monthly data:', response.data.monthly_data);

      if (response.data.success) {
        setSummaryTable(response.data.summary_table || {ESRD: {}, DIS: {}, AD: {}, ND: {}, TOTAL: {}});
        setMonthlyData(response.data.monthly_data || []);
        console.log('Monthly data set to state:', response.data.monthly_data?.length, 'records');
      } else {
        setError('Failed to load data: ' + (response.data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const getMonthName = (yr_mo) => {
    // Convert YYYYMM to "MMM YYYY" format
    const yr_mo_str = String(yr_mo || '');
    if (!yr_mo_str || yr_mo_str.length < 6) return yr_mo_str;
    const year = yr_mo_str.substring(0, 4);
    const month = yr_mo_str.substring(4, 6);
    const monthIndex = parseInt(month) - 1;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[monthIndex]} ${year}`;
  };

  // Cohort colors for ALL mode
  const cohortColors = {
    'ESRD': '#3b82f6',
    'DIS': '#10b981',
    'AD': '#f59e0b',
    'ND': '#8b5cf6',
    'TOTAL': '#ef4444'
  };

  // Filter monthly data by selected cohort
  const getFilteredMonthlyData = () => {
    if (cohortToggle === 'ALL') {
      return monthlyData;
    }
    return monthlyData.filter(item => item.cohort === cohortToggle);
  };

  // Get chart data for Monthly ER Visits
  const getMonthlyVisitsChartData = () => {
    const metricField = metricToggle === 'Total' ? 'er_visits' : 'avoidable_er_visits';
    
    console.log('getMonthlyVisitsChartData - monthlyData:', monthlyData.length, 'records');
    console.log('cohortToggle:', cohortToggle, 'metricToggle:', metricToggle);
    
    // Get unique months and sort them
    const months = [...new Set(monthlyData.map(item => item.month))].sort();
    console.log('Unique months:', months);
    
    if (cohortToggle === 'ALL') {
      // Show all cohorts as separate lines
      return months.map(month => {
        const dataPoint = { month: getMonthName(month) };
        ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].forEach(cohort => {
          const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohort);
          dataPoint[cohort] = cohortData ? (cohortData[metricField] || 0) : 0;
        });
        return dataPoint;
      });
    } else {
      // Show single cohort
      return months.map(month => {
        const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohortToggle);
        return {
          month: getMonthName(month),
          visits: cohortData ? (cohortData[metricField] || 0) : 0
        };
      });
    }
  };

  // Get chart data for Monthly ER Visits ADK
  const getMonthlyVisitsADKChartData = () => {
    const metricField = metricToggle === 'Total' ? 'er_visits_adk' : 'avoidable_er_visits_adk';
    
    const months = [...new Set(monthlyData.map(item => item.month))].sort();
    
    if (cohortToggle === 'ALL') {
      // Show all cohorts as separate lines
      return months.map(month => {
        const dataPoint = { month: getMonthName(month) };
        ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].forEach(cohort => {
          const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohort);
          dataPoint[cohort] = cohortData ? (cohortData[metricField] || 0) : 0;
        });
        return dataPoint;
      });
    } else {
      // Show single cohort
      return months.map(month => {
        const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohortToggle);
        return {
          month: getMonthName(month),
          adk: cohortData ? (cohortData[metricField] || 0) : 0
        };
      });
    }
  };

  // Get chart data for Monthly ER PMPY
  const getMonthlyPMPYChartData = () => {
    const metricField = metricToggle === 'Total' ? 'er_pmpy' : 'avoidable_er_pmpy';
    
    const months = [...new Set(monthlyData.map(item => item.month))].sort();
    
    if (cohortToggle === 'ALL') {
      // Show all cohorts as separate lines
      return months.map(month => {
        const dataPoint = { month: getMonthName(month) };
        ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].forEach(cohort => {
          const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohort);
          dataPoint[cohort] = cohortData ? (cohortData[metricField] || 0) : 0;
        });
        return dataPoint;
      });
    } else {
      // Show single cohort
      return months.map(month => {
        const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohortToggle);
        return {
          month: getMonthName(month),
          pmpy: cohortData ? (cohortData[metricField] || 0) : 0
        };
      });
    }
  };

  // Get chart data for Monthly ER Cost
  const getMonthlyCostChartData = () => {
    const metricField = metricToggle === 'Total' ? 'er_total_cost' : 'avoidable_er_total_cost';
    
    const months = [...new Set(monthlyData.map(item => item.month))].sort();
    
    if (cohortToggle === 'ALL') {
      // Show all cohorts as separate lines
      return months.map(month => {
        const dataPoint = { month: getMonthName(month) };
        ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].forEach(cohort => {
          const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohort);
          dataPoint[cohort] = cohortData ? (cohortData[metricField] || 0) : 0;
        });
        return dataPoint;
      });
    } else {
      // Show single cohort
      return months.map(month => {
        const cohortData = monthlyData.find(item => item.month === month && item.cohort === cohortToggle);
        return {
          month: getMonthName(month),
          cost: cohortData ? (cohortData[metricField] || 0) : 0
        };
      });
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: 'white',
          border: '1px solid #ccc',
          padding: '10px',
          borderRadius: '4px'
        }}>
          <p className="label" style={{fontWeight: 'bold', marginBottom: '5px'}}>{label}</p>
          {payload.map((entry, index) => {
            let formattedValue = typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value;
            // Format cost with dollar sign
            if (entry.name.toLowerCase().includes('cost') || entry.name.toLowerCase().includes('pmpy')) {
              formattedValue = `$${Math.round(entry.value).toLocaleString()}`;
            }
            return (
              <p key={index} style={{ color: entry.color, margin: '3px 0' }}>
                {entry.name}: {formattedValue}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const formatCurrencyAxis = (value) => {
    return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === 0) return '-';
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === 0) return '-';
    return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  const formatDecimal = (value, decimals = 3) => {
    if (value === null || value === undefined || value === 0) return '-';
    return value.toFixed(decimals);
  };

  if (loading) {
    return (
      <div style={{textAlign: 'center', padding: '80px'}}>
        <div style={{
          display: 'inline-block',
          width: '50px',
          height: '50px',
          border: '5px solid #f3f4f6',
          borderTop: '5px solid #4caf50',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }}></div>
        <div style={{fontSize: '18px', color: '#6b7280', fontWeight: '500'}}>Loading ER PY General data...</div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{padding: '40px', backgroundColor: '#fee', borderRadius: '8px', margin: '20px'}}>
        <h2 style={{color: '#c33'}}>Error loading data</h2>
        <p>{error}</p>
        <button onClick={fetchDashboardData} style={{padding: '10px 20px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}>Retry</button>
      </div>
    );
  }

  return (
    <div className="er-by3-vs-py-container">
      <div className="er-by3-vs-py-content">
        <h1>ER PY GENERAL</h1>

        {/* Summary Table - Fixed table with cohort columns */}
        <div className="summary-table-section">
          <table className="summary-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>ESRD</th>
                <th>DIS</th>
                <th>AD</th>
                <th>ND</th>
                <th>TOTAL</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Person Years</strong></td>
                <td>{formatNumber(summaryTable.ESRD?.person_years)}</td>
                <td>{formatNumber(summaryTable.DIS?.person_years)}</td>
                <td>{formatNumber(summaryTable.AD?.person_years)}</td>
                <td>{formatNumber(summaryTable.ND?.person_years)}</td>
                <td>{formatNumber(summaryTable.TOTAL?.person_years)}</td>
              </tr>
              <tr>
                <td><strong>Risk Score</strong></td>
                <td>{formatDecimal(summaryTable.ESRD?.risk_score, 3)}</td>
                <td>{formatDecimal(summaryTable.DIS?.risk_score, 3)}</td>
                <td>{formatDecimal(summaryTable.AD?.risk_score, 3)}</td>
                <td>{formatDecimal(summaryTable.ND?.risk_score, 3)}</td>
                <td>{formatDecimal(summaryTable.TOTAL?.risk_score, 3)}</td>
              </tr>
              <tr style={{backgroundColor: '#d1fae5'}}>
                <td><strong>ER TOTAL</strong></td>
                <td>{formatCurrency(summaryTable.ESRD?.er_total)}</td>
                <td>{formatCurrency(summaryTable.DIS?.er_total)}</td>
                <td>{formatCurrency(summaryTable.AD?.er_total)}</td>
                <td>{formatCurrency(summaryTable.ND?.er_total)}</td>
                <td>{formatCurrency(summaryTable.TOTAL?.er_total)}</td>
              </tr>
              <tr>
                <td><strong>ER VISITS</strong></td>
                <td>{formatNumber(summaryTable.ESRD?.er_visits)}</td>
                <td>{formatNumber(summaryTable.DIS?.er_visits)}</td>
                <td>{formatNumber(summaryTable.AD?.er_visits)}</td>
                <td>{formatNumber(summaryTable.ND?.er_visits)}</td>
                <td>{formatNumber(summaryTable.TOTAL?.er_visits)}</td>
              </tr>
              <tr style={{backgroundColor: '#d1fae5'}}>
                <td><strong>ER PMPY</strong></td>
                <td>{formatCurrency(summaryTable.ESRD?.er_pmpy)}</td>
                <td>{formatCurrency(summaryTable.DIS?.er_pmpy)}</td>
                <td>{formatCurrency(summaryTable.AD?.er_pmpy)}</td>
                <td>{formatCurrency(summaryTable.ND?.er_pmpy)}</td>
                <td>{formatCurrency(summaryTable.TOTAL?.er_pmpy)}</td>
              </tr>
              <tr>
                <td><strong>Avoidable ER Visits</strong></td>
                <td>{formatNumber(summaryTable.ESRD?.avoidable_er_visits)}</td>
                <td>{formatNumber(summaryTable.DIS?.avoidable_er_visits)}</td>
                <td>{formatNumber(summaryTable.AD?.avoidable_er_visits)}</td>
                <td>{formatNumber(summaryTable.ND?.avoidable_er_visits)}</td>
                <td>{formatNumber(summaryTable.TOTAL?.avoidable_er_visits)}</td>
              </tr>
              <tr style={{backgroundColor: '#d1fae5'}}>
                <td><strong>AVOIDABLE ER PMPY</strong></td>
                <td>{formatCurrency(summaryTable.ESRD?.avoidable_er_pmpy)}</td>
                <td>{formatCurrency(summaryTable.DIS?.avoidable_er_pmpy)}</td>
                <td>{formatCurrency(summaryTable.AD?.avoidable_er_pmpy)}</td>
                <td>{formatCurrency(summaryTable.ND?.avoidable_er_pmpy)}</td>
                <td>{formatCurrency(summaryTable.TOTAL?.avoidable_er_pmpy)}</td>
              </tr>
              <tr style={{backgroundColor: '#d1fae5'}}>
                <td><strong>AVOIDABLE ER TOTAL</strong></td>
                <td>{formatCurrency(summaryTable.ESRD?.avoidable_er_total)}</td>
                <td>{formatCurrency(summaryTable.DIS?.avoidable_er_total)}</td>
                <td>{formatCurrency(summaryTable.AD?.avoidable_er_total)}</td>
                <td>{formatCurrency(summaryTable.ND?.avoidable_er_total)}</td>
                <td>{formatCurrency(summaryTable.TOTAL?.avoidable_er_total)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Toggles for charts */}
        <div className="chart-controls" style={{margin: '30px 0', display: 'flex', gap: '40px', flexWrap: 'wrap'}}>
          <div className="toggle-group">
            <h3 style={{marginBottom: '10px'}}>Cohort Selection:</h3>
            <div className="toggle-buttons">
              {['ALL', 'ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].map(cohort => (
                <button
                  key={cohort}
                  className={`toggle-btn ${cohortToggle === cohort ? 'active' : ''}`}
                  onClick={() => setCohortToggle(cohort)}
                >
                  {cohort}
                </button>
              ))}
            </div>
          </div>
          <div className="toggle-group">
            <h3 style={{marginBottom: '10px'}}>Metric Type:</h3>
            <div className="toggle-buttons">
              <button
                className={`toggle-btn ${metricToggle === 'Total' ? 'active' : ''}`}
                onClick={() => setMetricToggle('Total')}
              >
                ER Total
              </button>
              <button
                className={`toggle-btn ${metricToggle === 'Avoidable' ? 'active' : ''}`}
                onClick={() => setMetricToggle('Avoidable')}
              >
                Avoidable
              </button>
            </div>
          </div>
        </div>

        {/* Monthly ER Visits Chart */}
        <div className="chart-section">
          <h2>Monthly ER Visits ({cohortToggle} - {metricToggle})</h2>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={getMonthlyVisitsChartData()} margin={{ top: 5, right: 30, left: 70, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" />
              <YAxis type="number" allowDecimals={false} domain={[0, dataMax => Math.ceil(dataMax * 1.15)]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {cohortToggle === 'ALL' ? (
                ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].map(cohort => (
                  <Line
                    key={cohort}
                    type="monotone"
                    dataKey={cohort}
                    stroke={cohortColors[cohort]}
                    strokeWidth={2}
                    dot={{ fill: cohortColors[cohort], r: 4 }}
                    activeDot={{ r: 6 }}
                    name={cohort}
                  />
                ))
              ) : (
                <Line
                  type="monotone"
                  dataKey="visits"
                  stroke="#1e40af"
                  strokeWidth={2}
                  dot={{ fill: '#1e40af', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="ER Visits"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly ER Visits ADK Chart */}
        <div className="chart-section">
          <h2>Monthly ER Visits ADK ({cohortToggle} - {metricToggle})</h2>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={getMonthlyVisitsADKChartData()} margin={{ top: 5, right: 30, left: 70, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" />
              <YAxis type="number" allowDecimals={false} domain={[0, dataMax => Math.ceil(dataMax * 1.15)]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {cohortToggle === 'ALL' ? (
                ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].map(cohort => (
                  <Line
                    key={cohort}
                    type="monotone"
                    dataKey={cohort}
                    stroke={cohortColors[cohort]}
                    strokeWidth={2}
                    dot={{ fill: cohortColors[cohort], r: 4 }}
                    activeDot={{ r: 6 }}
                    name={cohort}
                  />
                ))
              ) : (
                <Line
                  type="monotone"
                  dataKey="adk"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ fill: '#10b981', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="ER Visits ADK"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly ER PMPY Chart */}
        <div className="chart-section">
          <h2>Monthly ER PMPY ({cohortToggle} - {metricToggle})</h2>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={getMonthlyPMPYChartData()} margin={{ top: 5, right: 30, left: 70, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" />
              <YAxis type="number" allowDecimals={false} tickFormatter={formatCurrencyAxis} domain={[0, dataMax => Math.ceil(dataMax * 1.15)]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {cohortToggle === 'ALL' ? (
                ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].map(cohort => (
                  <Line
                    key={cohort}
                    type="monotone"
                    dataKey={cohort}
                    stroke={cohortColors[cohort]}
                    strokeWidth={2}
                    dot={{ fill: cohortColors[cohort], r: 4 }}
                    activeDot={{ r: 6 }}
                    name={cohort}
                  />
                ))
              ) : (
                <Line
                  type="monotone"
                  dataKey="pmpy"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ fill: '#f59e0b', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="ER PMPY"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly ER Cost Chart */}
        <div className="chart-section">
          <h2>Monthly ER Cost ({cohortToggle} - {metricToggle})</h2>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={getMonthlyCostChartData()} margin={{ top: 5, right: 30, left: 70, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" />
              <YAxis type="number" allowDecimals={false} tickFormatter={formatCurrencyAxis} domain={[0, dataMax => Math.ceil(dataMax * 1.15)]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {cohortToggle === 'ALL' ? (
                ['ESRD', 'DIS', 'AD', 'ND', 'TOTAL'].map(cohort => (
                  <Line
                    key={cohort}
                    type="monotone"
                    dataKey={cohort}
                    stroke={cohortColors[cohort]}
                    strokeWidth={2}
                    dot={{ fill: cohortColors[cohort], r: 4 }}
                    activeDot={{ r: 6 }}
                    name={cohort}
                  />
                ))
              ) : (
                <Line
                  type="monotone"
                  dataKey="cost"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={{ fill: '#ef4444', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="ER Total Cost"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default ERByThreeVsPy;
